import { useEffect, useRef, type ButtonHTMLAttributes, type ReactNode } from "react";

import type { AnlageStatus } from "../api/typen";
import { STATUS_TEXT } from "../lib/format";

export function Spinner({ klein = false, label }: { klein?: boolean; label?: string }) {
  return (
    <span className="inline-flex items-center gap-2" role="status">
      <span
        className={`${klein ? "h-4 w-4 border-2" : "h-6 w-6 border-[3px]"} inline-block animate-spin rounded-full border-neutral-300 border-t-neutral-900`}
        aria-hidden
      />
      {label ? <span className="text-sm text-neutral-600">{label}</span> : <span className="sr-only">Lädt…</span>}
    </span>
  );
}

type Variante = "primaer" | "sekundaer" | "gefahr" | "geist";

const VARIANTEN: Record<Variante, string> = {
  primaer: "bg-marke text-white hover:bg-marke-hover disabled:bg-neutral-400",
  sekundaer: "border border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-50 disabled:text-neutral-400",
  gefahr: "border border-ampel-rot/40 bg-white text-ampel-rot hover:bg-ampel-rotHell disabled:text-neutral-400",
  geist: "text-neutral-700 hover:bg-neutral-100 disabled:text-neutral-400",
};

export function Knopf({
  variante = "sekundaer",
  laedt = false,
  children,
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante; laedt?: boolean }) {
  return (
    <button
      type="button"
      {...rest}
      disabled={rest.disabled || laedt}
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed ${VARIANTEN[variante]} ${className}`}
    >
      {laedt && <Spinner klein />}
      {children}
    </button>
  );
}

const STATUS_FARBEN: Record<AnlageStatus, string> = {
  draft: "bg-neutral-100 text-neutral-700 ring-neutral-300",
  validiert: "bg-sky-50 text-sky-800 ring-sky-200",
  final: "bg-ampel-gruenHell text-ampel-gruen ring-ampel-gruen/30",
  angemeldet: "bg-violet-50 text-violet-800 ring-violet-200",
};

export function StatusBadge({ status }: { status: AnlageStatus }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${STATUS_FARBEN[status]}`}>
      {STATUS_TEXT[status]}
    </span>
  );
}

export function Karte({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <section className={`rounded-2xl border border-neutral-200 bg-white shadow-karte ${className}`}>{children}</section>;
}

export function Modal({
  offen,
  titel,
  beiSchliessen,
  children,
  fuss,
  breit = false,
}: {
  offen: boolean;
  titel: string;
  beiSchliessen: () => void;
  children: ReactNode;
  fuss?: ReactNode;
  breit?: boolean;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (offen && !el.open) {
      el.showModal();
      // showModal() fokussiert das erste fokussierbare Element – das wäre „✕“. Reacts autoFocus
      // greift im <dialog> nicht, deshalb markieren Formulare ihr Startfeld mit data-autofokus.
      el.querySelector<HTMLElement>("[data-autofokus]")?.focus();
    }
    if (!offen && el.open) el.close();
  }, [offen]);

  return (
    <dialog
      ref={dialog}
      onClose={beiSchliessen}
      onCancel={(e) => {
        e.preventDefault();
        beiSchliessen();
      }}
      className={`max-h-[85vh] max-w-none rounded-2xl p-0 shadow-2xl backdrop:bg-black/40 ${
        breit ? "w-[min(56rem,calc(100vw-2rem))]" : "w-[min(32rem,calc(100vw-2rem))]"
      }`}
      aria-labelledby="modal-titel"
    >
      {offen && (
        <div className="flex max-h-[85vh] flex-col">
          <header className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
            <h2 id="modal-titel" className="text-lg font-semibold">
              {titel}
            </h2>
            <button type="button" onClick={beiSchliessen} aria-label="Schließen" className="rounded p-1 text-neutral-500 hover:bg-neutral-100">
              ✕
            </button>
          </header>
          <div className="overflow-y-auto px-6 py-4">{children}</div>
          {fuss && <footer className="flex justify-end gap-2 border-t border-neutral-200 px-6 py-4">{fuss}</footer>}
        </div>
      )}
    </dialog>
  );
}

export function Hinweis({ art, children }: { art: "fehler" | "warnung" | "info" | "erfolg"; children: ReactNode }) {
  const klassen = {
    fehler: "border-ampel-rot/30 bg-ampel-rotHell text-ampel-rot",
    warnung: "border-ampel-gelb/30 bg-ampel-gelbHell text-ampel-gelb",
    info: "border-neutral-200 bg-neutral-50 text-neutral-700",
    erfolg: "border-ampel-gruen/30 bg-ampel-gruenHell text-ampel-gruen",
  }[art];
  return <div className={`rounded-xl border px-4 py-3 text-sm ${klassen}`}>{children}</div>;
}
